import { useContext, useMemo } from 'react';
import { Navigate, Route, BrowserRouter as Router, Routes, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import _ from 'lodash';
import UserDataContextManager, { UserDataContext } from './context/UserContextProvider';
import ToastDataContextManager from './context/ToastContextProvider';
import HRLogin from './HRLogin';
import HRMain from './HRMain';
import ResetPassword from './ResetPassword';
import ForgotPassword from './ForgotPassword';
import { SWRConfig } from 'swr';
import { useTranslation } from 'react-i18next';
export function classNames(...classes) {
  return classes.filter(Boolean).join(' ')
}

// Build login path with redirect param (only allow relative paths to avoid open redirect)
const loginPathWithRedirect = (pathname, search) => {
  const full = pathname + search;
  if (!full || full === '/') return '/login';
  const safe = full.startsWith('/') && !full.startsWith('//') ? encodeURIComponent(full) : '';
  return safe ? `/login?redirect=${safe}` : '/login';
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const userContext = useContext(UserDataContext);
  const location = useLocation();

  const isLoggedIn = !_.isEmpty(userContext.user) && userContext.user.id !== 0;

  // If not logged in, redirect to corresponding login page
  if (!isLoggedIn) {
    return <Navigate to={loginPathWithRedirect(location.pathname, location.search)} replace />;
  }

  // If logged in but wrong role, redirect to proper dashboard
  if (userContext.user.user_type !== 'HR') {
    return <Navigate to={`/login`} replace />;
  }

  return children;
};

function AppWrapper() {
  const userContext = useContext(UserDataContext)
  const navigate = useNavigate()
  const location = useLocation()

  const { t } = useTranslation();

  return <SWRConfig
    value={{
      fetcher: async (url) => {
        return fetch(url, {
          headers: {
            Authorization: `Bearer ${userContext.user?.token}`,
          },
        }).then(async (r) => {
          if (r.status === 401) {
            userContext.setUserData({})
            navigate(loginPathWithRedirect(location.pathname, location.search))
            throw Error(t('sessionExpired'))
          }
          if (!r.ok) throw Error(await r.text());
          else return r.json();
        });
      },
      revalidateOnFocus: false,
      fallbackData: []
    }}
  >
    <HRMain />
  </SWRConfig>
}

// When already logged in and on login page, redirect to `redirect` param (if safe) or home
function NavigateToRedirectOrHome() {
  const [searchParams] = useSearchParams();
  const redirect = searchParams.get('redirect');
  const to = redirect && redirect.startsWith('/') && !redirect.startsWith('//') ? redirect : '/';
  return <Navigate to={to} replace />;
}

function NavigationApp() {
  const userContext = useContext(UserDataContext)

  const isLoggedIn = useMemo(() => {
    return !_.isEmpty(userContext.user) && userContext.user.id !== 0 && userContext.user.user_type === 'HR'
  }, [userContext.user])

  return (
    <Router>
      <Routes>
        <Route path="/login" element={isLoggedIn ? <NavigateToRedirectOrHome /> : <HRLogin />} />
        <Route path="/reset-password/:token" element={isLoggedIn ? <Navigate to="/" replace /> : <ResetPassword />} />
        <Route path="/forgot-password" element={isLoggedIn ? <Navigate to="/" replace /> : <ForgotPassword />} />
        <Route path="/*" element={
          <ProtectedRoute>
            <AppWrapper />
          </ProtectedRoute>
        } />
      </Routes>
    </Router>
  );
}

export default function App() {
  return (
    <ToastDataContextManager>
      <UserDataContextManager>
        <NavigationApp />
      </UserDataContextManager>
    </ToastDataContextManager>
  );
}